import Vue from 'vue'
import { GetterTree, MutationTree, ActionTree } from 'vuex';
import { RootState } from '@/store'
import Product from '@/models/Product'
import ProductService from '@/services/ProductService'
const productService = new ProductService()

export interface ProductState {
  products: Array<Product>;
}

const state: ProductState = {
  products: [],
}

const mutations: MutationTree<ProductState> = {
  ADD_PRODUCT(state: any, product: Product) {
    const index = state.products.findIndex((obj: Product) => obj.id === product.id)
    if (index < 0) {
      state.products.push(product)
    }
  },
  UPDATE_PRODUCT(state: any, product: Product) {
    const index = state.products.findIndex((obj: Product) => obj.id === product.id)
    if (index > -1) {
      Vue.set(state.products, index, product)
    }
  },
  DELETE_PRODUCT(state: any, product: Product) {
    const index = state.products.findIndex((obj: Product) => obj.id === product.id)
    if (index > -1) {
      Vue.delete(state.products, index)
    }
  },
  RESET_STATE(state: any) {
    state.products = []
  }
}

const actions: ActionTree<ProductState, any> = {
  clearProducts({ commit }) {
    commit('RESET_STATE')
    productService.clearLastDocRef();
  },

  async fetchProducts({ commit }, payload: any): Promise<any> {
    const { projectId, options } = payload
    let sortBy, sortDesc = null;

    if (!projectId) return;

    if (options && options.sortBy && options.sortDesc && options.sortBy.length === 1 && options.sortDesc.length === 1) {
      sortBy = options.sortBy[0]
      sortDesc = options.sortDesc[0]
    }

    const limit = payload.limit ? payload.limit : 10;

    return await productService.fetchProducts(projectId, sortBy, sortDesc, limit)
      .then((items: any) => {
        if (items) {
          items.forEach((item: Product) => {
            commit('ADD_PRODUCT', item)
          })
          return Promise.resolve(items)
        }
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  async queryProducts({ commit }, payload: any): Promise<any> {
    const { projectId, sortBy, sortDesc, limit } = payload
    if (!projectId) return;

    return await productService.queryProducts(projectId, sortBy, sortDesc, limit)
      .then((items: any) => {
        return Promise.resolve(items)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  async fetchProductsByDeliveryDate({ commit }, payload: any): Promise<any> {
    const { projectId, limit } = payload
    if (!projectId) return;

    return await productService.fetchProductsByDeliveryDate(projectId, limit)
      .then((items: any) => {
        return Promise.resolve(items)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  async fetchProduct({ commit, state }, params) {
    const index = state.products.findIndex((obj: Product) => obj.id === params.itemId)
    if (index < 0) {
      await productService.fetchProduct(params.projectId, params.itemId)
        .then((item: Product) => {
          commit('ADD_PRODUCT', item)
        })
    }
  },
  async createProduct({ commit }, item: Product): Promise<any> {
    return await productService.createProduct(item)
      .then(result => {
        item.id = result.id
        commit('ADD_PRODUCT', item)
        return Promise.resolve(item)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  async editProduct({ commit }, item: Product): Promise<any> {
    return await productService.editProduct(item)
      .then(() => {
        commit('UPDATE_PRODUCT', item)
        return Promise.resolve(item)
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  async deleteProduct({ commit }, item: Product): Promise<any> {
    return await productService.deleteProduct(item)
      .then(() => {
        commit('DELETE_PRODUCT', item)
        return Promise.resolve()
      })
      .catch((error) => {
        return Promise.reject(error)
      })
  },
  async deleteMultipleItems({ dispatch }, data: Array<Product>) {
    try {
      for await (const item of data) {
        dispatch('deleteProduct', item)
      }
    }
    catch (error) {
      console.error('Error in deleteMultipleItems: ' + error)
      return false
    }
  },
}

const getters: GetterTree<ProductState, RootState> = {
  getProducts: (state: any) => {
    return state.products
  },
  getProduct: (state: any) => (id: string) => {
    return state.products.find((item: any) => item.id === id);
  },
  getProductsByDelivery: (state: any) => {
    return state.products.slice().sort((a: any, b: any) => {
      return (new Date(a.deliveryDate) as any) - (new Date(b.deliveryDate) as any)
    }).slice(0, 5);
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
